import React from 'react';
import { Container, Box, Typography, Paper, Divider } from '@mui/material';

const TermsPage = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper sx={{ p: { xs: 3, md: 6 } }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Terms and Conditions
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          Last updated: November 3, 2025
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography paragraph>
          Thank you for reading these comprehensive Terms and for using IvyAstrology.com! These Terms and Conditions ("Terms") govern your access to and use of the IvyAstrology.com website (the "Site") and all products and services provided through the Site. IvyAstrology.com is owned and operated by C & E Technology Ltd (trading as IvyAstrology.com), a company registered in Ireland.
        </Typography>

        <Typography paragraph>
          By accessing or using our Site or purchasing any astrology products or services from us, you agree to be bound by these Terms. If you do not agree, please do not use our Site or services. Please read these Terms carefully. They contain important information about your rights and obligations, as well as limitations and exclusions that may apply to you.
        </Typography>

        <Typography paragraph>
          If you have any questions about these Terms, please contact us using the information in Section 17 below.
        </Typography>

        {/* Section 1 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            1. Definitions
          </Typography>
          <Typography paragraph>
            In these Terms:
          </Typography>
          <Typography component="div" paragraph>
            <strong>"Company", "we", "us", or "our"</strong> refers to C & E Technology Ltd, trading as IvyAstrology.com.
          </Typography>
          <Typography component="div" paragraph>
            <strong>"Site"</strong> refers to the website IvyAstrology.com and any related mobile or online applications through which our services are offered.
          </Typography>
          <Typography component="div" paragraph>
            <strong>"Services"</strong> refers to all astrology-related services, content, and products made available by us, including but not limited to horoscope readings, astrological forecasts, personalized predictions, reports, subscriptions, and any other spiritual or metaphysical advisory services.
          </Typography>
          <Typography component="div" paragraph>
            <strong>"User", "you", or "your"</strong> refers to any person who accesses, uses, or purchases from the Site or our Services.
          </Typography>
          <Typography paragraph>
            By using the Site or Services, you confirm that you understand and accept these definitions and the Terms as a whole.
          </Typography>
        </Box>

        {/* Section 2 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            2. Eligibility and Age Requirement
          </Typography>
          <Typography paragraph>
            Our Services are intended for individuals 18 years of age or older. By using this Site or purchasing our Services, you represent and warrant that you are at least 18 years old (or the age of majority in your jurisdiction, if higher). The Company does not knowingly provide services to minors. If you are under 18, you must refrain from using this Site and our Services. We reserve the right to request proof of age at any stage to ensure compliance with this requirement.
          </Typography>
        </Box>

        {/* Section 3 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            3. Scope of Services
          </Typography>
          <Typography paragraph>
            IvyAstrology.com offers a broad range of astrology products and services – essentially any astrology or metaphysical product available on this planet. Our offerings may include: personalized horoscope readings, natal chart interpretations, tarot or oracle card readings, astrological compatibility analyses, yearly or monthly forecast reports, subscriptions to regular astrological updates, and other related content.
          </Typography>
          <Typography paragraph>
            All Services are provided for personal, non-commercial use only. Please note that all astrological readings, forecasts, and related services are provided for entertainment and informational purposes. Astrology is a subjective interpretive practice; while we strive to offer insightful and high-quality content, we do not guarantee that any predictions or advice will be accurate or will produce any particular outcomes. See Section 10 (Disclaimers) for more details.
          </Typography>
          <Typography paragraph>
            We reserve the right to modify, expand, or reduce the scope of Services at any time. All Services offered are subject to availability, and we may discontinue or change any Service without prior notice.
          </Typography>
        </Box>

        {/* Section 4 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            4. Account Registration and Security
          </Typography>
          <Typography paragraph>
            Some features of our Site (such as purchasing certain products, accessing subscription content, or managing orders) may require you to create an account. If you choose or are provided with a username, password, or any other piece of security information as part of our security procedures, you must treat such information as confidential.
          </Typography>
          <Typography paragraph>
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. By creating an account, you agree to provide accurate, current, and complete information about yourself as prompted, and to update such information promptly if it changes.
          </Typography>
          <Typography paragraph>
            You may not impersonate anyone else, create an account for anyone other than yourself, or provide an email address other than your own. You are responsible for all usage of the Site through your account. If you believe that your account is being used without your authorization, or if you suspect any security breach, you must notify us immediately. We are not liable for any loss or damage arising from your failure to comply with the above requirements.
          </Typography>
        </Box>

        {/* Section 5 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            5. Orders, Pricing, and Payment
          </Typography>
          <Typography paragraph>
            When you purchase any astrology product or Service from our Site (for example, a horoscope report or subscription), you are making an offer to buy the selected Service. All orders are subject to acceptance by the Company. We reserve the right to refuse or cancel any order at our discretion, for example, if a Service is mispriced, no longer available, or if we suspect fraudulent or unauthorized activity.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Pricing:
          </Typography>
          <Typography paragraph>
            All prices for our Services will be indicated on the Site in the applicable currency (e.g. US Dollars ($) or Euros (€)). Prices are subject to change at any time at our discretion, but no price change will affect an order you have already paid for. In the event a price was incorrectly displayed, we will inform you and may cancel the order (with a full refund of any amount charged).
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Payment:
          </Typography>
          <Typography paragraph>
            Payment must be made at the time of order, via the payment methods we provide (such as credit/debit card, PayPal, or other third-party payment processors). By providing payment information, you represent and warrant that you are authorized to use the payment method and you authorize us (or our third-party payment processors) to charge the full amount to the payment method you designate for the purchase, including any applicable taxes or fees.
          </Typography>
          <Typography paragraph>
            Payment processing is handled by third-party providers (including Stripe), so we do not directly collect or store your full financial information (such as credit card numbers). We are not responsible for any errors or security breaches by those third-party payment processors, but will use reasonable efforts to ensure that we partner with reputable providers.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Taxes:
          </Typography>
          <Typography paragraph>
            Prices are generally inclusive of any VAT or sales tax where applicable, unless stated otherwise. If any taxes, duties, or charges apply to your purchase (based on your country or state of residence), you are responsible for paying them. We will comply with our tax obligations as required by law. We will issue electronic receipts or confirmations for purchases. Please ensure that your contact and billing information is correct and up-to-date so you can receive all communications regarding your order.
          </Typography>
        </Box>

        {/* Section 6 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            6. Subscriptions and Auto-Renewals
          </Typography>
          <Typography paragraph>
            We may offer certain Services on a subscription basis (for example, monthly or yearly astrology forecast subscriptions or memberships providing periodic content). If you sign up for a subscription Service, it will automatically renew at the end of each billing cycle (e.g. monthly or annually) unless you cancel the subscription prior to the renewal date.
          </Typography>
          <Typography paragraph>
            By purchasing a subscription, you agree that your payment method on file will be automatically charged the applicable subscription fees at each renewal, until you cancel.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Cancellation of Subscription:
          </Typography>
          <Typography paragraph>
            You may cancel a subscription at any time by logging into your account (if self-service cancellation is available) or by contacting us using the contact information in Section 17. Cancellation will prevent future subscription charges, but no prorated refund will be provided for the remaining period in a subscription cycle that has already begun.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Changes to Subscription:
          </Typography>
          <Typography paragraph>
            We reserve the right to adjust the pricing or content of subscription Services. If we change the subscription price or material terms, we will provide you with advance notice by email or through the Site. If you do not agree with the changes, you may cancel the subscription before the next billing date.
          </Typography>
        </Box>

        {/* Section 7 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            7. Delivery of Services and Electronic Communications
          </Typography>
          <Typography paragraph>
            Our astrology products and Services are generally delivered electronically. This may include delivery via email, providing access through the Site, or communication through messaging platforms. By providing us with your contact information and using our Services, you consent to receive communications from us electronically.
          </Typography>
          <Typography paragraph>
            You agree that all agreements, notices, receipts, and other communications that we provide to you electronically satisfy any legal requirement that such communications be in writing.
          </Typography>
          <Typography paragraph>
            We will make reasonable efforts to deliver Services within the advertised or agreed timeframe. However, all delivery times are estimates and we do not guarantee any specific turnaround time. It is your responsibility to provide us with correct and accessible contact information.
          </Typography>
        </Box>

        {/* Section 8 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            8. Cancellation, Refunds and Consumer Rights
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Cancellation by You (Non-Subscription Purchases):
          </Typography>
          <Typography paragraph>
            For one-time purchases of digital astrology products (such as a personalized report or horoscope reading), all sales are final once the Service has been delivered. Due to the personalized and digital nature of our Services, we do not offer refunds on completed readings or delivered content, except in exceptional circumstances or where required by law.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            EU/EEA/UK Consumers – Right of Withdrawal:
          </Typography>
          <Typography paragraph>
            If you are a consumer resident in the European Union, European Economic Area, or United Kingdom, you may have the legal right to withdraw from a purchase of digital content within 14 days of purchase (the "cooling-off period") under EU consumer protection laws. However, if you purchase digital content or services from us for immediate delivery or download, you expressly agree to waive your right of withdrawal once the service has been fully performed or the download/link has been provided.
          </Typography>
          <Typography paragraph>
            Nothing in these Terms affects any statutory rights you may have under applicable law, including rights regarding faulty or misdescribed services, which cannot be excluded or limited by contract.
          </Typography>
        </Box>

        {/* Section 9 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            9. Intellectual Property Rights
          </Typography>
          <Typography paragraph>
            All content on the Site and delivered through our Services – including but not limited to text, graphics, logos, images, videos, software, digital downloads, reports, and compilations – is the intellectual property of the Company or its content suppliers and is protected by copyright, trademark, and other intellectual property laws.
          </Typography>
          <Typography paragraph>
            IvyAstrology.com and associated logos or designs are trademarks or service marks owned by us. All rights are expressly reserved.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Personal Use License:
          </Typography>
          <Typography paragraph>
            When you purchase or access an astrological report, reading, or other content from us, we grant you a personal, non-exclusive, non-transferable, revocable license to use that content for your own personal and non-commercial use only. You may not copy, distribute, share, publish, resell, or otherwise use any content from our Site or Services for any commercial purpose or for the benefit of any third party without our prior written consent.
          </Typography>
        </Box>

        {/* Section 10 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            10. User Conduct and Prohibited Uses
          </Typography>
          <Typography paragraph>
            You agree to use IvyAstrology.com and our Services only for lawful purposes and in accordance with these Terms. You must not misuse the Site or Services. Prohibited activities include (but are not limited to):
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <Typography paragraph>
              <strong>• Illegal Activities:</strong> Using the Site or Services for any unlawful purpose, or to promote or encourage any illegal activity.
            </Typography>
            <Typography paragraph>
              <strong>• Harassment or Harm:</strong> Engaging in any conduct that is harassing, defamatory, abusive, threatening, or harmful toward us, our staff, other users, or any other individuals or groups.
            </Typography>
            <Typography paragraph>
              <strong>• Misrepresentation:</strong> Impersonating the Company or any other person, or misrepresenting your affiliation with any person or entity.
            </Typography>
            <Typography paragraph>
              <strong>• Interference:</strong> Attempting to interfere with the proper working of the Site or our Services, including transmitting any virus, malware, or harmful code.
            </Typography>
            <Typography paragraph>
              <strong>• Unauthorized Access:</strong> Attempting to gain unauthorized access to any portion or feature of the Site or any of our servers by hacking, password "mining" or any other illegitimate means.
            </Typography>
            <Typography paragraph>
              <strong>• Data Mining:</strong> Using any automated system (like robots, spiders, or scrapers) to access the Site for data harvesting or scraping.
            </Typography>
            <Typography paragraph>
              <strong>• Resale or Commercial Use:</strong> Exploiting the Site or Services for any commercial purpose not expressly authorized by the Company.
            </Typography>
          </Typography>
          <Typography paragraph>
            Violation of this Section may result in termination of your access to the Site and Services and may subject you to civil and/or criminal liability.
          </Typography>
        </Box>

        {/* Section 11 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            11. Disclaimers (Astrology Services and No Professional Advice)
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600, color: 'error.main' }}>
            FOR ENTERTAINMENT PURPOSES ONLY:
          </Typography>
          <Typography paragraph>
            All astrology readings, horoscopes, predictions, and related services provided by IvyAstrology.com are intended for entertainment, motivational and informational purposes only. While many people find value and insight in astrological guidance, it is not an exact science. No guarantee is given as to the accuracy, completeness, or reliability of any astrological or spiritual advice or information provided.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Not a Substitute for Professional Advice:
          </Typography>
          <Typography paragraph>
            Our Services and any information on the Site do not constitute professional advice of any kind. Specifically, nothing we provide is intended to be, or should be taken as, medical, psychological, financial, investment, legal, or other professional advice. Do not disregard or delay seeking professional advice because of something you have read on our Site or received through our Services.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Personal Responsibility:
          </Typography>
          <Typography paragraph>
            You acknowledge that you are fully responsible for any decisions or actions you take based on the information provided by our Services. The Company will not be liable for any outcomes (positive or negative) that may occur as a result of following or relying on any astrological insight, advice, or prediction you obtained from us. Always use your own judgment and common sense.
          </Typography>
        </Box>

        {/* Section 12 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            12. Disclaimer of Warranties (General)
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Use At Your Own Risk:
          </Typography>
          <Typography paragraph>
            The Site, its content, and all Services are provided on an "as is" and "as available" basis. To the fullest extent permitted by law, the Company disclaims all warranties of any kind, whether express or implied, regarding the Site and Services. This includes, but is not limited to, implied warranties of accuracy, completeness, reliability, merchantability, fitness for a particular purpose, and non-infringement.
          </Typography>
          <Typography paragraph>
            We do not warrant that the Site or Services will meet your expectations or requirements, or that the operation of the Site will be uninterrupted, timely, secure, or error-free.
          </Typography>
        </Box>

        {/* Section 13 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            13. Limitation of Liability
          </Typography>
          <Typography paragraph>
            To the maximum extent permitted by law, in no event will C & E Technology Ltd (IvyAstrology.com) or its directors, officers, employees, affiliates, agents, or content providers be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages whatsoever arising out of or related to your use of (or inability to use) the Site or our Services, even if we have been advised of the possibility of such damages.
          </Typography>
          <Typography paragraph>
            In addition, our total aggregate liability to you for all claims arising from or related to the Site or Services is limited to the amount you paid us (if any) for the specific Service or content giving rise to the claim. If the claim does not relate to a purchase, then our liability will be limited to €100 (EUR one hundred) or the equivalent in your local currency.
          </Typography>
          <Typography paragraph>
            We do not exclude or limit our liability to you where it would be illegal to do so. This means that nothing in these Terms will exclude or limit our liability for: (a) death or personal injury caused by our negligence; (b) fraud or fraudulent misrepresentation; or (c) any other liability which cannot be excluded or limited under applicable law.
          </Typography>
        </Box>

        {/* Section 14 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            14. Indemnification
          </Typography>
          <Typography paragraph>
            You agree to indemnify, defend, and hold harmless C & E Technology Ltd (IvyAstrology.com) and its officers, directors, employees, consultants, affiliates, and agents from and against any and all third-party claims, liabilities, damages, losses, or expenses (including reasonable solicitors' or attorneys' fees and costs) that arise out of or are related to: (a) your use or misuse of the Site or Services; (b) your violation of these Terms; (c) your violation of any law or regulation or the rights of any third party in connection with your use of the Site/Services; or (d) any content that you upload or post to the Site (if applicable).
          </Typography>
        </Box>

        {/* Section 15 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            15. Privacy and Data Protection
          </Typography>
          <Typography paragraph>
            In order to provide our Services, we will need to collect and process certain personal information from you. This may include information such as your name, contact details (email, phone number), birth date, birth time, birth location, and any other information you provide that is relevant to the astrological service.
          </Typography>
          <Typography paragraph>
            By using our Site and Services, you acknowledge that we will collect, use, store, and process your personal data for the purposes of providing the Services and as otherwise described in our Privacy Policy.
          </Typography>
          <Typography paragraph>
            We are committed to protecting your privacy and handling your personal data in accordance with applicable data protection laws, including the EU General Data Protection Regulation (GDPR) and Ireland's Data Protection Acts. Please review our Privacy Policy (available on the Site) for detailed information on how we collect, use, protect, and disclose your information.
          </Typography>
          <Typography paragraph>
            We use reasonable administrative, technical, and physical measures to safeguard personal data against loss, theft, and unauthorized access, use, or modification. For instance, our Site and databases are hosted on secure servers provided by reputable third parties like AWS (Amazon Web Services). However, no data transmission or storage can be guaranteed 100% secure.
          </Typography>
        </Box>

        {/* Section 16 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            16. Changes to Terms and Termination
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Changes to Terms:
          </Typography>
          <Typography paragraph>
            The Company reserves the right to modify or update these Terms at any time. If we make material changes, we will notify users by posting the updated Terms on the Site and updating the "Last updated" date at the top of the Terms. It is your responsibility to review these Terms periodically for any changes. Your continued use of the Site or Services after any modifications to the Terms constitutes acceptance of those changes.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Termination by You:
          </Typography>
          <Typography paragraph>
            You may stop using our Services at any time. If you have an account, you can also request that we delete your account (subject to our data retention obligations as described in the Privacy Policy).
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Termination or Suspension by Us:
          </Typography>
          <Typography paragraph>
            We may terminate or suspend your access to the Site and Services (including your account, if you have one) at our sole discretion, with or without notice, if we believe that you have violated these Terms or if we decide to discontinue the Service or the Site.
          </Typography>
        </Box>

        {/* Section 17 */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            17. Governing Law and Jurisdiction
          </Typography>
          <Typography paragraph>
            These Terms and any dispute or claim (including non-contractual disputes or claims) arising out of or in connection with the Site, Services, or these Terms shall be governed by and construed in accordance with the laws of Ireland. This choice of law is made without regard to conflicts of laws principles, and with the explicit provision that the U.N. Convention on Contracts for the International Sale of Goods shall not apply.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 600 }}>
            Jurisdiction:
          </Typography>
          <Typography paragraph>
            You and the Company both agree that the courts of Ireland shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these Terms or your use of the Services. Notwithstanding the foregoing, if you are a consumer residing in the European Union, you may also be entitled to bring claims in your country of residence under certain consumer protection laws.
          </Typography>
        </Box>

        {/* Section 18 - Contact */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            18. Contact Information
          </Typography>
          <Typography paragraph>
            If you have any questions, concerns, or feedback about these Terms or any other aspect of our Services, please contact us:
          </Typography>
          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2 }}>
            <Typography paragraph sx={{ mb: 1 }}>
              <strong>C & E Technology Ltd</strong> (trading as IvyAstrology.com)
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              <strong>Address:</strong> 28 Croftwell Square, Rathcoole, Co. Dublin, D24 F6PN, Ireland
            </Typography>
            <Typography paragraph sx={{ mb: 0 }}>
              <strong>Email:</strong> <a href="mailto:contact@ivyastrology.com" style={{ color: '#667eea' }}>contact@ivyastrology.com</a>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
          Thank you for choosing IvyAstrology.com. We appreciate your trust in our Services and will do our utmost to provide you with insightful astrological content while safeguarding our mutual rights through these Terms. By using our Site, you acknowledge that you have read, understood, and agree to these Terms and Conditions. We wish you a positive and enlightening experience!
        </Typography>
      </Paper>
    </Container>
  );
};

export default TermsPage;
